<template>
  <div class="register-area">
    <loader v-show="isFormLoading"></loader>
    <div v-show="!isFormLoading">
      <error :errors="errors"></error>
      <b-alert
          show
          variant="warning"
          fade
          v-if="warning"
      >
        {{ warning }}
      </b-alert>
      <div class="pep-section" v-if="!returnToPreviousSession && identity_status!=='cancelled'">
        <div class="start-investing">{{ $t('identity.toStartInvesting') }}</div>
        <div>{{ $t('identity.veriffIsA') }}</div>
        <br/>
        <div>
          <ul>
            <li>{{ $t('identity.haveYourIdentity') }}</li>
            <li>{{ $t('identity.theVerificationTakes') }}</li>
          </ul>
        </div>
      </div>
      <div v-if="identity_status==='cancelled'">
        <strong>{{ $t('identity.weNoticedSomething') }}</strong>
      </div>
      <div v-if="docExpired">
        <strong>{{ $t('identity.pleaseStartVerification') }}</strong>
      </div>
      <div class="veriff-section">
        <div>{{ $t('identity.yourCurrentVerificationStatus') }}
          <span v-bind:class="{ 'badge-success': identity_status==='approved' }" class="badge">
            {{ showStatus(identity_status) }}
          </span>
        </div>
        <div v-if="identity_status==='pending'">
          <div v-if="!returnToPreviousSession">{{ $t('identity.weAreProcessing') }}</div>
          <div v-if="returnToPreviousSession">
            <br /><strong>{{ $t('identity.weNoticedSomething1') }}</strong>
            <br />
            <br />
            <div class="w-100 text-center">
              <b-button class="income-btn" @click="openContinueVerification">{{ $t('identity.continueVerification') }}</b-button>
            </div>
          </div>
        </div>
        <div v-if="badStatuses.includes(identity_status)">{{ $t('identity.tryAgainOr') }}</div>
        <div class="text-center veriff-btn" v-if="(identity_status!=='approved' && identity_status!=='pending' || docExpired)" id='veriff-root'>
          <b-button class="veriff-submit" @click="startVerification">{{ $t('identity.submitBtnText') }}</b-button>
        </div>
      </div>
      <div class="w-100" style="display: inline-block">
        <div class="text-right w-100" style="display: inline-block">
          <b-button class="skip-btn" to="account">{{ $t('identity.skipForNow') }}</b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {createVeriffFrame, MESSAGES} from '@veriff/incontext-sdk'
import Error from './Error'
import verification from '../api/verification'
import flagMixin from '@/mixins/flag'
import Loader from '@/components/Loader'
import imageMixin from '@/mixins/image'
import investorStatuses, {registrationStatuses} from '@/config/investorStatuses'
import investor from '@/api/investor'
import classificatorDataHelper from '@/helpers/classificatorDataHelper'

export default {
  components: {Loader, Error},
  mixins: [flagMixin, imageMixin],
  data: () => ({
    submitted: false,
    isFormLoading: false,
    decision_message: 'Person has not been verified.',
    reason_message: 'Not verified',
    errors: [],
    warning: '',
    identity_status: 'Not verified',
    pep_enabled: null,
    pep_help: false,
    selectedCountry: null,
    countryItems: null,
    badStatuses: ['abandoned', 'expired', 'declined', 'resubmission requested'],
    returnToPreviousSession: false,
    docExpired: false,
    submitBtnText: ''
  }),
  props: ['investorData'],
  computed: {},
  mounted()
  {
    this.submitBtnText = this.$t('identity.submitBtnText')
    this.countryItems = classificatorDataHelper.getCountriesWithPopularInTop()
    if (this.investorData && this.investorData.investor_identity_status)
    {
      this.identity_status = this.investorData.investor_identity_status
    }
    this.getInvestorInfo()
  },
  watch: {
    '$store.state.investors.investor': function (investor) {
      // This page allowed only for registrationStatuses
      if (investor && investor.investor_account_status)
      {
        if (!registrationStatuses.includes(investor.investor_account_status))
        {
          if (investor.investor_account_status === investorStatuses.APPROVED && investor.days_until_doc_expired && investor.days_until_doc_expired <= 30)
          {
            this.docExpired = this.returnToPreviousSession = true
            // skip hard redirect for approved but with investor.days_until_doc_expired <= 30
          }
          else
          {
            // console.log('This page allowed only for registrationStatuses', investor.investor_account_status)
            console.log('Hard redirect to account from identity tab  :', investor.investor_account_status)
            this.$router.push('/account')
          }
        }
      }
    },
    investorData: function (investor)
    {
      if (investor && investor.investor_identity_status)
      {
        this.identity_status = investor.investor_identity_status
      }
      if (investor && investor.veriff_url)
      {
        this.returnToPreviousSession = true
      }
    }
  },
  methods: {
    onSubmit(evt)
    {
      evt.preventDefault()
      if (!this.form.pep_frontend.country)
      {
        this.$refs.country.$el.classList.add('form-control', 'is-invalid')
        return false
      }
      else
      {
        this.$refs.country.$el.classList.remove('form-control', 'is-invalid')
      }
      this.isFormLoading = true
      this.$emit('setstep', 3)
      this.submitted = true
      this.isFormLoading = false
    },
    async getInvestorInfo()
    {
      this.isFormLoading = true
      verification.getInfo().then(response =>
          {
            if (response)
            {
              if (response.status && this.investorData)
              {
                this.identity_status = response.status
                this.$emit('updateinvestor', {identity_status: response.status})
                if (this.badStatuses.includes(response.status))
                {
                  this.errors.push('Your verification process has failed. Please retry Veriff authentication.')
                }
              }
              this.decision_message = response.decision_message || ''
              this.reason_message = response.reason_message || ''
            }
            this.isFormLoading = false
          }
      )
          .catch((error) =>
          {
            this.errors.push(error)
            if (this.investorData)
            {
              this.identity_status = error.message || 'unknown error'
            }
            this.isFormLoading = false
          })
    },
    openContinueVerification()
    {
      if (this.investorData && this.investorData.veriff_url)
      {
        window.open(this.investorData.veriff_url, '_blank')
        this.returnToPreviousSession = false
      }
    },
    showStatus: function (value)
    {
      if (!value)
      {
        return ''
      }
      if (this.$t)
      {
        value = value.toString().trim().split(' ').join('_').toLowerCase()
        return this.$t('identity.status.' + value)
      }
      else {
        value = value.toString().replace('_', ' ')
        return value.charAt(0).toUpperCase() + value.slice(1)
      }
      // console.log('Run showStatus: ', this.$t('identity.status.' + value))
    },
    startVerification()
    {
      verification.createIdentification().then(response =>
          {
            createVeriffFrame({
              url: response.verification.url,
              onEvent: function (msg)
              {
                switch (msg)
                {
                  case MESSAGES.STARTED:
                    console.log('session status changed to STARTED!')
                    console.log('We are processing the verification information and will inform you if anything further is required. You can continue to set up the account.')
                    // console.log(createResponse)
                    if (response.verification && response.verification.id && response.verification.url)
                      {
                        investor.update({veriff_url: response.verification.url}).then().catch((error) => { this.errors.push(error) })
                      }
                    this.warning = ''
                    this.identity_status = 'pending'
                    break
                  case MESSAGES.FINISHED:
                    console.log('session status changed to FINISHED!')
                    console.log('You can now continue to set up the account.')
                    this.$emit('setstep', 3)
                    this.submitted = true
                    this.warning = ''
                    this.identity_status = 'pending'
                    investor.update({veriff_url: null}).then().catch((error) => { this.errors.push(error) })
                    investor.getInvestor().then(response =>
                    {
                      if (response)
                      {
                        this.$store.state.investors.investor.investor_account_status = response.investor_account_status
                        this.$store.state.investors.investor.problems = response.problems
                        this.$router.push('/account')
                      }
                    })
                    break
                  case MESSAGES.CANCELED:
                    // this.warning = 'Please try again or skip it for now. Please note that you can not invest until we have verified your identity.'
                    this.identity_status = 'cancelled'
                    investor.update({veriff_url: null}).then().catch((error) => { this.errors.push(error) })
                    verification.cancelIdentification(response.verification.id)
                    // we need a time to choose status from pending to cancelled
                    // setTimeout(() => {
                    //   veriff.mount({
                    //     submitBtnText: this.submitBtnText
                    //   })
                    // }, 500, this)
                    window.scrollTo(0, 0)
                    break
                }
              }.bind(this)
            })
          }
      )
    }
  }
}
</script>
<style>
.veriff-container{
  text-align: center;
}
.veriff-submit{
  max-width: 350px;
  font-weight: bold;
  background: #30A7FE !important;
  color: #FFFFFF !important;
  box-shadow: none !important;
  border-color: #30A7FE !important;
}
</style>
<style lang="scss" scoped>
.pep-section {
  display: inline-block;
  margin-top: 15px;
  margin-bottom: 2px;
  width: 100%;
}

.pep-type-form {
  display: inline-block;
  width: 100%;
  margin-top: 15px;
  margin-bottom: 2px;
}

.help {
  width: 20px;
  cursor: pointer;
}

#veriff-root {
  width: 100%;
  max-width: none;
  min-width: auto;
}

.start-investing {
  font-weight: bold;
  margin-top: 5px;
  margin-bottom: 5px;
}

.veriff-section {
  margin-top: 15px;
}
.veriff-btn{
  margin-top: 15px;
}
</style>
